import { EscalationLevelConfig } from '../models/configuration-row';
import { EscalationLevel } from '../models/sla-escalation.model';

const ESCALATION_LEVEL_ORDER: Record<EscalationLevel, number> = {
  LEVEL_1: 1,
  LEVEL_2: 2,
  LEVEL_3: 3,
};

const ESCALATION_LEVEL_TITLES: Record<EscalationLevel, string> = {
  LEVEL_1: '1st Escalation',
  LEVEL_2: '2nd Escalation',
  LEVEL_3: '3rd Escalation',
};

export function getEscalationLevelTitle(level: EscalationLevel): string {
  return ESCALATION_LEVEL_TITLES[level];
}

export function getTriggerHint(triggerPercent: number): string {
  return `Escalate when ${triggerPercent}% of SLA time is consumed`;
}

export function validateEscalationTriggerPercents(levels: EscalationLevelConfig[]): string | null {
  const sorted = sortEscalationLevels(levels);
  for (const level of sorted) {
    if (!Number.isFinite(level.triggerPercent)) {
      return 'Trigger Time (% of SLA) is required for all escalation levels';
    }
    if (level.triggerPercent < 0) {
      return 'Trigger Time (% of SLA) must be 0 or greater';
    }
  }

  const level1 = sorted.find((level) => level.escalationLevel === 'LEVEL_1');
  const level2 = sorted.find((level) => level.escalationLevel === 'LEVEL_2');
  const level3 = sorted.find((level) => level.escalationLevel === 'LEVEL_3');

  // Later levels must fire at the same time or later (higher % of SLA consumed).
  if (level1 && level2 && level1.triggerPercent > level2.triggerPercent) {
    return 'Trigger Time (% of SLA) for Level 1 must be less than or equal to Level 2';
  }
  if (level2 && level3 && level2.triggerPercent > level3.triggerPercent) {
    return 'Trigger Time (% of SLA) for Level 2 must be less than or equal to Level 3';
  }
  if (level1 && level3 && !level2 && level1.triggerPercent > level3.triggerPercent) {
    return 'Trigger Time (% of SLA) for Level 1 must be less than or equal to Level 3';
  }

  return null;
}

export function sortEscalationLevels(levels: EscalationLevelConfig[]): EscalationLevelConfig[] {
  return [...levels].sort(
    (left, right) =>
      ESCALATION_LEVEL_ORDER[left.escalationLevel] - ESCALATION_LEVEL_ORDER[right.escalationLevel],
  );
}

export function sortEscalationLevelRules<T extends { escalation_level: EscalationLevel }>(
  rules: T[],
): T[] {
  return [...rules].sort(
    (left, right) =>
      ESCALATION_LEVEL_ORDER[left.escalation_level] -
      ESCALATION_LEVEL_ORDER[right.escalation_level],
  );
}

export function createDefaultEscalationLevels(): EscalationLevelConfig[] {
  return [
    {
      id: 'default-level-1',
      title: ESCALATION_LEVEL_TITLES.LEVEL_1,
      escalationLevel: 'LEVEL_1',
      triggerPercent: 75,
      triggerHint: getTriggerHint(75),
      escalateTo: [],
      alertRecipients: [],
      channels: {
        email: true,
        sms: false,
        phoneCall: false,
        pushNotification: false,
      },
      isBreach: false,
      isActive: true,
    },
    {
      id: 'default-level-2',
      title: ESCALATION_LEVEL_TITLES.LEVEL_2,
      escalationLevel: 'LEVEL_2',
      triggerPercent: 90,
      triggerHint: getTriggerHint(90),
      escalateTo: [],
      alertRecipients: [],
      channels: {
        email: true,
        sms: true,
        phoneCall: false,
        pushNotification: false,
      },
      isBreach: false,
      isActive: true,
    },
    {
      id: 'default-level-3',
      title: ESCALATION_LEVEL_TITLES.LEVEL_3,
      escalationLevel: 'LEVEL_3',
      triggerPercent: 100,
      triggerHint: getTriggerHint(100),
      escalateTo: [],
      alertRecipients: [],
      channels: {
        email: true,
        sms: true,
        phoneCall: true,
        pushNotification: true,
      },
      isBreach: true,
      isActive: true,
    },
  ];
}
